/**
 * Deterministic largest-remainder (Hamilton) apportionment.
 *
 * Scales each value proportionally to `total`, floors each scaled share to
 * `scale` decimal places, then distributes the remaining units (largest
 * fractional remainders first) so the returned list sums to exactly `total`
 * at the given scale.
 *
 * This is the client-side mirror of the backend percentage distribution so
 * that recomputed percentage columns always sum to the target (e.g. 100.00).
 *
 * Properties:
 *  - The returned list has the same length as `values`.
 *  - The sum of the returned values equals `total` within 0.01 at `scale`.
 *  - Golden case: largestRemainder([9001065, 7412197]) === [54.84, 45.16].
 *
 * Edge cases:
 *  - An empty `values` array returns an empty array.
 *  - When every value is zero (sum is zero), units are distributed evenly
 *    across the entries so the result still sums to `total` without NaN.
 */
export function largestRemainder(
  values: number[],
  total = 100,
  scale = 2,
): number[] {
  const n = values.length;
  if (n === 0) {
    return [];
  }

  // Work in integer "units" at the requested scale to avoid float drift.
  const factor = Math.pow(10, scale);
  const totalUnits = Math.round(total * factor);

  // Guard against negative inputs; treat them as zero contribution.
  const safeValues = values.map((v) =>
    Number.isFinite(v) && v > 0 ? v : 0,
  );
  const sum = safeValues.reduce((acc, v) => acc + v, 0);

  // All-zero (or empty-weight) input: distribute units as evenly as possible.
  if (sum <= 0) {
    const base = Math.floor(totalUnits / n);
    const remaining = totalUnits - base * n;
    return safeValues.map((_, i) => {
      const extra = i < remaining ? 1 : 0;
      return (base + extra) / factor;
    });
  }

  // Scale each value to units, floor it, and track the fractional remainder.
  const scaled = safeValues.map((v, index) => {
    const exact = (v / sum) * totalUnits;
    const floored = Math.floor(exact);
    return { index, floored, remainder: exact - floored };
  });

  const allocated = scaled.reduce((acc, s) => acc + s.floored, 0);
  let leftover = totalUnits - allocated;

  // Distribute leftover units to the largest fractional remainders first.
  // Ties are broken by lower index for determinism.
  const order = [...scaled].sort((a, b) => {
    if (b.remainder !== a.remainder) {
      return b.remainder - a.remainder;
    }
    return a.index - b.index;
  });

  const unitsByIndex = new Array<number>(n);
  scaled.forEach((s) => {
    unitsByIndex[s.index] = s.floored;
  });

  let i = 0;
  while (leftover > 0) {
    const target = order[i % n];
    unitsByIndex[target.index] += 1;
    leftover -= 1;
    i += 1;
  }

  return unitsByIndex.map((units) => units / factor);
}
